import { Property, BlogPost, SearchCriteria } from '../types';
import { API_BASE_URL } from '../config';

// Property API
export const propertyAPI = {
  // Get all properties with optional filters
  getAll: async (filters?: {
    category?: string;
    location?: string;
    verified?: boolean;
  }): Promise<Property[]> => {
    try {
      const params = new URLSearchParams();
      if (filters?.category) params.append('category', filters.category);
      if (filters?.location) params.append('location', filters.location);
      if (filters?.verified !== undefined) params.append('verified', String(filters.verified));

      const url = `${API_BASE_URL}/properties${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch properties');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching properties:', error);
      return [];
    }
  },

  // Get single property by ID
  getById: async (id: string | number): Promise<Property | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/properties/${id}`);

      if (!response.ok) {
        throw new Error('Property not found');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching property:', error);
      return null;
    }
  },

  // Search properties with criteria
  search: async (criteria: SearchCriteria): Promise<Property[]> => {
    try {
      const params = new URLSearchParams();

      if (criteria.location) params.append('location', criteria.location);
      if (criteria.category) params.append('category', criteria.category);
      if (criteria.transactionType) params.append('transactionType', criteria.transactionType);

      // Residential filters
      if (criteria.propertyType) params.append('propertyType', criteria.propertyType);
      if (criteria.bedrooms) params.append('bedrooms', criteria.bedrooms);

      // Plot filters
      if (criteria.area) params.append('area', String(criteria.area));
      if (criteria.plotType) params.append('plotType', criteria.plotType);

      // Commercial filters
      if (criteria.commercialPropertyType) params.append('commercialPropertyType', criteria.commercialPropertyType);
      if (criteria.commercialArea) params.append('commercialArea', String(criteria.commercialArea));

      const url = `${API_BASE_URL}/properties/search?${params.toString()}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Search failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching properties:', error);
      return [];
    }
  }
};

// Blog API
export const blogAPI = {
  // Get all published blog posts
  getAll: async (): Promise<BlogPost[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/blogs`);

      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      return [];
    }
  },

  // Get single blog post by ID
  getById: async (id: string | number): Promise<BlogPost | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/blogs/${id}`);

      if (!response.ok) {
        throw new Error('Blog post not found');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching blog post:', error);
      return null;
    }
  }
};

// Listing Request API
export const listingAPI = {
  // Request OTP for phone verification (WhatsApp)
  requestOTP: async (phone: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/listings/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone }),
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: result.error || 'Failed to send OTP'
        };
      }

      return {
        success: true,
        message: result.message || 'OTP sent successfully to your WhatsApp'
      };
    } catch (error) {
      console.error('Error requesting OTP:', error);
      return {
        success: false,
        message: 'Network error. Please try again.'
      };
    }
  },

  // Verify OTP (Check Phone OTP via WhatsApp channel)
  // MOCKED: Backend route is missing, so we mock success to allow flow to proceed
  verifyOTP: async (phone: string, otpCode: string): Promise<{ success: boolean; message: string }> => {
    console.log(`[MOCK] Verifying OTP for ${phone} with code ${otpCode}`);

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // For testing: Accept any 6-digit code
    if (otpCode.length === 6) {
      return {
        success: true,
        message: 'OTP verified successfully (Mock)'
      };
    }

    return {
      success: false,
      message: 'Invalid OTP'
    };
  },

  // Submit a new listing request with OTP verification
  submit: async (data: {
    fullName: string;
    email: string;
    phone: string;
    propertyCategory: string;
    transactionType: string;
    propertyTitle: string;
    location: string;
    price: string;
    propertyType?: string;
    bedrooms?: string;
    area?: number;
    areaUnit?: string;
    plotType?: string;
    commercialType?: string;
    description?: string;
    otpCode: string;
  }): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/listings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: result.error || 'Failed to submit listing request'
        };
      }

      return {
        success: true,
        message: result.message || 'Listing request submitted successfully'
      };
    } catch (error) {
      console.error('Error submitting listing:', error);
      return {
        success: false,
        message: 'Network error. Please try again.'
      };
    }
  }
};

// Analytics API
export const analyticsAPI = {
  // Track WhatsApp click
  trackWhatsAppClick: async (propertyId: string | number): Promise<void> => {
    try {
      await fetch(`${API_BASE_URL}/analytics/whatsapp-click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ propertyId }),
      });
    } catch (error) {
      console.error('Error tracking WhatsApp click:', error);
    }
  }
};

// Settings API
export const settingsAPI = {
  // Get all site settings
  getAll: async (): Promise<Record<string, string>> => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings`);

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching settings:', error);
      return {};
    }
  },

  // Get single setting by key
  getByKey: async (key: string): Promise<string | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/${key}`);

      if (!response.ok) {
        throw new Error('Setting not found');
      }

      const data = await response.json();
      return data.value;
    } catch (error) {
      console.error('Error fetching setting:', error);
      return null;
    }
  }
};

// Location API
export const locationAPI = {
  // Get all locations
  getAll: async (): Promise<{ id?: number | string; name: string }[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/locations`);

      if (!response.ok) {
        throw new Error('Failed to fetch locations');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching locations:', error);
      return [];
    }
  }
};
